import Editor, { Monaco } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { cn } from '@/lib/utils';
import { useTheme } from '@/components/theme-provider';
import { useEffect, useRef } from 'react';

interface CodeEditorProps {
  value: string;
  onChange?: (value: string) => void;
  language?: 'json' | 'javascript' | 'text';
  readOnly?: boolean;
  height?: string;
  maxHeight?: string;
  className?: string;
  placeholder?: string;
}

export function CodeEditor({
  value,
  onChange,
  language = 'json',
  readOnly = true,
  height = 'auto',
  maxHeight = '400px',
  className,
  placeholder = '',
}: CodeEditorProps) {
  const { theme } = useTheme();
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  
  // Determine if we should use dark theme
  const isDark = theme === 'dark' || 
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  
  // Handle editor mount
  const handleEditorDidMount = (editor: editor.IStandaloneCodeEditor) => {
    editorRef.current = editor;
  };

  // Handle editor will mount - configure Monaco
  const handleEditorWillMount = (monaco: Monaco) => {
    // Configure JSON language features
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemas: [],
      enableSchemaRequest: false,
      schemaValidation: 'error',
      trailingCommas: 'error',
      comments: 'error'
    });

    // Configure JavaScript/TypeScript features
    monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false
    });

    monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ES2020,
      allowNonTsExtensions: true,
      allowJs: true
    });
  };

  // Update editor layout when height changes
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.layout();
    }
  }, [height, maxHeight]);

  // Calculate actual height
  const actualHeight = height === 'auto' ? '200px' : height;

  return (
    <div 
      className={cn(
        'overflow-hidden rounded-md border',
        isDark ? 'border-white/10' : 'border-gray-200',
        className
      )}
      style={{ maxHeight }}
    >
      <Editor
        height={actualHeight}
        defaultLanguage={language === 'text' ? 'plaintext' : language}
        language={language === 'text' ? 'plaintext' : language}
        theme={isDark ? 'vs-dark' : 'light'}
        value={value}
        onChange={(value) => onChange?.(value || '')}
        onMount={handleEditorDidMount}
        beforeMount={handleEditorWillMount}
        options={{
          readOnly,
          minimap: { enabled: false },
          fontSize: 14,
          lineNumbers: 'on',
          renderLineHighlight: readOnly ? 'none' : 'all',
          scrollBeyondLastLine: false,
          wordWrap: 'on',
          wrappingStrategy: 'advanced',
          automaticLayout: true,
          padding: { top: 12, bottom: 12 },
          lineDecorationsWidth: readOnly ? 0 : 10,
          lineNumbersMinChars: 3,
          glyphMargin: false,
          folding: true,
          renderWhitespace: 'none',
          cursorBlinking: readOnly ? 'solid' : 'blink',
          smoothScrolling: true,
          contextmenu: !readOnly,
          quickSuggestions: !readOnly,
          suggestOnTriggerCharacters: !readOnly,
          acceptSuggestionOnEnter: 'smart',
          tabCompletion: 'on',
          wordBasedSuggestions: 'off',
          parameterHints: {
            enabled: !readOnly
          },
          hover: {
            enabled: true
          },
          overviewRulerLanes: 0,
          hideCursorInOverviewRuler: true,
          overviewRulerBorder: false,
          scrollbar: {
            vertical: 'auto',
            horizontal: 'auto',
            verticalScrollbarSize: 10,
            horizontalScrollbarSize: 10,
            alwaysConsumeMouseWheel: false
          },
          // Add placeholder support
          ...(placeholder && !value ? {
            ariaLabel: placeholder,
          } : {})
        }}
      />
    </div>
  );
}